import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radar, X, Heart, Undo2 } from 'lucide-react';
import { SwipeCard } from './SwipeCard';
import { useSwipe } from '../../hooks/useSwipe';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { rotateTopCandidate, undoSkip, fetchMoreCandidates } from '../../store/slices/peopleSlice';
import { useTelegram } from '../../hooks/useTelegram';
export function SwipeStack() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { hapticImpact } = useTelegram();
    const { candidates, loadingMore, exhausted, skippedHistory } = useAppSelector((s) => s.people);
    const topUser = candidates[0];
    const nextUser = candidates[1];
    const topUserRef = useRef(topUser);
    topUserRef.current = topUser;
    // Auto-load more when running low
    useEffect(() => {
        if (candidates.length <= 2 && !loadingMore && !exhausted) {
            const exclude = candidates.map((c) => c.id);
            dispatch(fetchMoreCandidates(exclude));
        }
    }, [candidates.length, loadingMore, exhausted]);
    // Preload photos for upcoming candidates (next 4)
    useEffect(() => {
        const upcoming = candidates.slice(1, 5);
        for (const u of upcoming) {
            const photos = u.photos?.length
                ? u.photos
                : u.photoUrl ? [{ photoUrl: u.photoUrl }] : [];
            for (const p of photos) {
                if (p.photoUrl) {
                    const img = new Image();
                    img.src = p.photoUrl;
                }
            }
        }
    }, [candidates]);
    // Color Flood refs
    const floodLeftRef = useRef(null);
    const floodRightRef = useRef(null);
    const iconSkipRef = useRef(null);
    const iconInviteRef = useRef(null);
    const hapticFiredRef = useRef(false);
    const handleSkip = useCallback(() => {
        if (!topUserRef.current)
            return;
        hapticImpact('light');
        dispatch(rotateTopCandidate());
    }, []);
    const handleInvite = useCallback(() => {
        const u = topUserRef.current;
        if (!u)
            return;
        hapticImpact('medium');
        navigate(`/people/invite/${u.id}`);
    }, []);
    const handleDragProgress = useCallback((progress, direction) => {
        const fL = floodLeftRef.current;
        const fR = floodRightRef.current;
        const iS = iconSkipRef.current;
        const iI = iconInviteRef.current;
        if (!fL || !fR || !iS || !iI)
            return;
        // Haptic at ~50% threshold
        if (progress > 0.5 && !hapticFiredRef.current) {
            hapticFiredRef.current = true;
            hapticImpact('light');
        }
        if (progress < 0.3) {
            hapticFiredRef.current = false;
        }
        const p = progress;
        if (direction < 0) {
            // Skip (left)
            fL.style.opacity = String(Math.min(p * 1.5, 1));
            iS.style.opacity = String(p);
            iS.style.transform = `translate(-50%,-50%) scale(${0.3 + p * 0.7})`;
            iS.style.filter = `drop-shadow(0 0 ${60 * p}px rgba(255,255,255,${0.6 * p})) drop-shadow(0 0 ${120 * p}px rgba(255,255,255,${0.2 * p}))`;
            const rays = iS.querySelector('.swipe-rays');
            if (rays) {
                rays.style.transform = `scale(${1 + p * 0.8})`;
                rays.style.opacity = String(p);
            }
            fR.style.opacity = '0';
            iI.style.opacity = '0';
        }
        else if (direction > 0) {
            // Invite (right)
            fR.style.opacity = String(Math.min(p * 1.5, 1));
            iI.style.opacity = String(p);
            iI.style.transform = `translate(-50%,-50%) scale(${0.3 + p * 0.7})`;
            iI.style.filter = `drop-shadow(0 0 ${60 * p}px rgba(236,72,153,${0.7 * p})) drop-shadow(0 0 ${120 * p}px rgba(236,72,153,${0.3 * p}))`;
            const rays = iI.querySelector('.swipe-rays');
            if (rays) {
                rays.style.transform = `scale(${1 + p * 0.8})`;
                rays.style.opacity = String(p);
            }
            fL.style.opacity = '0';
            iS.style.opacity = '0';
        }
        else {
            fL.style.opacity = '0';
            fR.style.opacity = '0';
            iS.style.opacity = '0';
            iI.style.opacity = '0';
        }
    }, [hapticImpact]);
    const swipeHandlers = useSwipe({
        onSwipeLeft: handleSkip,
        onSwipeRight: handleInvite,
        onDragProgress: handleDragProgress,
    });
    if (!topUser) {
        return (_jsxs("div", { className: "flex-1 flex flex-col items-center justify-center px-8", children: [_jsx(Radar, { className: "w-12 h-12 text-white/25 mb-4" }), _jsx("p", { className: "text-white/50 text-[15px] font-medium text-center", children: "\u0412 \u044D\u0442\u043E\u043C \u0433\u043E\u0440\u043E\u0434\u0435 \u043F\u043E\u043A\u0430 \u043D\u0438\u043A\u043E\u0433\u043E \u043D\u0435\u0442" }), _jsxs("p", { className: "text-white/35 text-[13px] mt-2 text-center leading-relaxed", children: ["\u041E\u0442\u043F\u0440\u0430\u0432\u044C\u0442\u0435 \u0438\u043D\u0432\u0430\u0439\u0442 \u0434\u0440\u0443\u0433\u0443 \u0432 \u044D\u0442\u043E\u043C \u0433\u043E\u0440\u043E\u0434\u0435 \u2014", '\n', "\u0438 \u043E\u043D \u043F\u043E\u044F\u0432\u0438\u0442\u0441\u044F \u0437\u0434\u0435\u0441\u044C \u043F\u0435\u0440\u0432\u044B\u043C"] })] }));
    }
    return (_jsxs("div", { className: "flex-1 relative min-h-0", children: [nextUser && (_jsx("div", { className: "stack-edge", style: {
                    left: '18px',
                    height: '80%',
                    top: '24px',
                    zIndex: 1,
                    opacity: 0.5,
                } })), nextUser && (_jsx(SwipeCard, { user: nextUser, isTop: false, onTouchStart: () => { }, onTouchMove: () => { }, onTouchEnd: () => { }, onMouseDown: () => { } }, nextUser.id)), _jsx(SwipeCard, { user: topUser, isTop: true, onTouchStart: swipeHandlers.onTouchStart, onTouchMove: swipeHandlers.onTouchMove, onTouchEnd: swipeHandlers.onTouchEnd, onMouseDown: swipeHandlers.onMouseDown }, topUser.id), skippedHistory.length > 0 && (_jsx("button", { onClick: () => { hapticImpact('light'); dispatch(undoSkip()); }, className: "absolute top-3 left-3 z-10 w-10 h-10 rounded-full flex items-center justify-center active:scale-90 transition-transform", style: { background: 'rgba(0,0,0,.4)', backdropFilter: 'blur(8px)' }, children: _jsx(Undo2, { className: "w-5 h-5 text-white/70" }) })), _jsx("div", { ref: floodLeftRef, className: "swipe-flood left" }), _jsx("div", { ref: floodRightRef, className: "swipe-flood right" }), _jsxs("div", { ref: iconSkipRef, className: "swipe-giant-icon skip", children: [_jsx("div", { className: "swipe-rays" }), _jsx(X, { className: "icon-x", strokeWidth: 1.5, style: { width: 120, height: 120 } })] }), _jsxs("div", { ref: iconInviteRef, className: "swipe-giant-icon invite", children: [_jsx("div", { className: "swipe-rays" }), _jsx(Heart, { className: "icon-heart", style: { width: 120, height: 120 } })] })] }));
}
